import * as queries from './queries';
import * as medHistoryQueries from '../medical-history/queries';
import app from '../../app';

import {
  getHealthParameterNodeByCode,
  getHealthParameterValueByCode,
} from '../utils';

import { notPerformedReasons } from './questionnaires/walking-test';
import clinicalAssessment, {getClinicalAssessmentQuestion} from './questionnaires/clinical-assessment';
import medicationDSS, {getMedicationDSSQuestion} from './questionnaires/medication-dss';

export async function init({ commit, dispatch, getters, rootGetters }, { patientId, version }) {
  const previousOpenEncounterVersion = getters.version;
  const previousOpenEncounterPatientId = getters.patientId;

  commit('setPatientId', patientId);
  commit('setVersion', version);

  const existingEncounter = await dispatch('findExistingEncounter');

  const sameAsPreviousOpenEncounter =
    parseInt(version) === parseInt(previousOpenEncounterVersion) &&
    patientId === previousOpenEncounterPatientId;

  if (existingEncounter || !sameAsPreviousOpenEncounter) {
    await dispatch('getEncounterData', {hiddenDataOnly : false});
  } else {
    /* Even for new encounters we still need the hidden data, this is because this data (visit 1 EDC data) is used to decide when to show or hide certain questions */
    await dispatch('getEncounterData', {hiddenDataOnly : true, skipReset: false});
  }
}

export function findExistingEncounter({ getters, rootGetters }) {
  const { version } = getters;
  const encounters = rootGetters['patientSummary/completedSDMEncounters'];
  const currentEncounter = rootGetters['patientSummary/getCurrentInProgressSDMEncounter'];
  const isCurrentValid = parseInt(currentEncounter?.version) === parseInt(version);

  const completed = encounters.find((item) => parseInt(item.version) === parseInt(version));
  const current = isCurrentValid ? currentEncounter : null;

  // Check for completed or currently in progress encounter
  return completed || current;
}

export async function getEncounterData({ dispatch, commit }, {hiddenDataOnly, skipReset}) {
  if (!skipReset) {
    commit('reset')
  }

  /* This is hidden data that always needs to be loaded when an encounter Clinical Assessment/DSS Information tab is opened. */
  const fetchDataPromises = [
    dispatch('getQuestionnaireV1V2', 'CLINICAL-ASSESSMENT'),
    dispatch('getQuestionnaireV1V2', 'DSS-INFO'),

    // Make sure server info is loaded (contains DSS version, necessary to decide which questions to show)
    dispatch('loadServerInfo', null, {root: true}),
];

  /* Visible form data */
  if (!hiddenDataOnly){
    const fetchVisibleData = [
      dispatch('getVitalSigns'),
      dispatch('getWalkingTest'),
      dispatch('getQuestionnaire', 'CLINICAL-ASSESSMENT'),
      dispatch('getQuestionnaire', 'DSS-INFO'),
      dispatch('getEncounterMyDSSInfo'),
      dispatch('updateCHA2DS2VASCFromInput')
    ]

    for (let i=0; i<fetchVisibleData.length; i++){
      fetchDataPromises.push(fetchVisibleData[i]);
    }
  }

  await Promise.all(fetchDataPromises);
}

export async function getEncounterMyDSSInfo({ dispatch, commit, getters }) {
  const { patientId } = getters;

  const age = getters.age ? parseInt(getters.age) : null;
  const includeExtraSystolicDayValues = getters.bloodPressureSystolic ? [parseFloat(getters.bloodPressureSystolic)] : null;
  const includeExtraDiastolicDayValues = getters.bloodPressureDiastolic ? [parseFloat(getters.bloodPressureDiastolic)] : null;


  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.getEncounterMyDSSInfo,
      variables: { patientId : parseInt(patientId), age : parseInt(age), includeExtraDiastolicDayValues, includeExtraSystolicDayValues },
    });

    if (data && !errors) {
      const myDSSInfo = data.myDSSInfo

      commit('setMeanBloodPressureAboveGoal', myDSSInfo.meanBloodPressureAboveGoal);
      commit('setCurrentAmountReceivedHypertensionDrugs', myDSSInfo.currentAmountReceivedHypertensionDrugs);

    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getVitalSigns({ dispatch, commit, getters, rootState }) {
  const { patientId, version } = getters;

  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.getVitalSigns,
      variables: {
        patientId : patientId.toString(),
        version : version.toString(),
        LDLUnit : app.store.getters['patientSummary/cholesterolLDLPreferredUnit']
      },
    });

    if (data && !errors) {
      const nodes = data.myHealthParameters.edges.map((item) => item.node);
      const bodyWeight = getHealthParameterValueByCode(nodes, '29463-7');
      const pulseRate = getHealthParameterValueByCode(nodes, '8867-4');
      const LDL = getHealthParameterValueByCode(nodes, '18262-6');
      const LDLInPreferredUnit = getHealthParameterValueByCode(nodes, '18262-6', 'valueQuantityPreferredUnit');
      const eGFR = getHealthParameterValueByCode(nodes, '69405-9');
      const bloodPressureNode = getHealthParameterNodeByCode(nodes, '85354-9');
      const component = bloodPressureNode.component || [];
      const systolic = getHealthParameterValueByCode(component, '8480-6');
      const diastolic = getHealthParameterValueByCode(component, '8462-4');

      commit('setBodyWeight', bodyWeight);
      commit('setPulseRate', pulseRate);
      commit('setBloodPressureSystolic', systolic);
      commit('setBloodPressureDiastolic', diastolic);
      commit('setLDL', LDL);
      commit('setLDLInPreferredUnit', LDLInPreferredUnit);
      commit('setEGFR', eGFR);

    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getWalkingTest({ dispatch, commit, getters }) {
  const { patientId, version } = getters;

  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.getWalkingTest,
      variables: { patientId : patientId.toString(), version : version.toString() },
    });

    if (data && !errors) {
      const test = data.mySixMinuteWalkingTests[0] || {};
      const { distanceWalked, notPerformedReason, perceivedExertion } = test;

      if (distanceWalked) {
        commit('setIsWalkingTestPerformed', true);
        commit('setDistanceWalked', distanceWalked);
      }

      if (notPerformedReason) {
        commit('setIsWalkingTestPerformed', false);
        Object.keys(notPerformedReasons).forEach((key) => {
          if (notPerformedReasons[key].includes(notPerformedReason)) {
            commit('setNotPerformedReasonGroup', key);
          }
        });
        commit('setNotPerformedReason', notPerformedReason);
      }

      if (perceivedExertion) {
        commit('setPerceivedExertion', perceivedExertion);
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

// export async function getMergedQuestionnaire({ dispatch, commit, rootGetters }, key) {
//   const subjectId = rootGetters['patientSummary/studyId'];
//   let version = rootGetters['encounter/version'];
//   if (!version) version = null;
//
//   try {
//     const { data, errors } = await app.apolloClient.query({
//       query: medHistoryQueries.getMergedQuestionnaire,
//       variables: { subjectId, key, version },
//     });
//
//     if (data && !errors) {
//       const questionnaire = data.mergedQuestionnaireForm.mergedForm;
//
//       if (questionnaire && questionnaire.entries) {
//         questionnaire.entries.forEach((entry) => {
//           if (entry.givenAnswers && entry.givenAnswers.length) {
//             let answer = entry.givenAnswers[0].key ? entry.givenAnswers[0].key : entry.givenAnswers[0].openAnswer;
//
//             if (getClinicalAssessmentQuestion(entry.question.key) || getMedicationDSSQuestion(entry.question.key)){
//               commit(`SET_${entry.question.key}`, answer);
//             }
//           }
//         });
//       }
//     } else if (errors.length) {
//       errors.forEach((error) => {
//         dispatch('error/submit', error, { root: true });
//       });
//     }
//   } catch (error) {
//     dispatch('error/submit', error, { root: true });
//   }
// }

export async function updateCHA2DS2VASCFromInput({ getters, rootGetters, dispatch }) {
  return dispatch('getCHA2DS2VASC', {
    patientId : rootGetters['patientSummary/id'],
    age: getters.AGE ? getters.AGE : null,
    hasCongestiveHeartFailure: getters.HISTORY_OF_HF === 'YES' || getters.NEW_DIAGNOSIS_HF === 'YES',
    hasHistoryHypertension: getters.HYPERTENSION === 'YES',
    hasHistoryDiabetes : getters.DIABETES_MELLITUS_TYPE1 === 'YES' || getters.DIABETES_MELLITUS_TYPE2 === 'YES',
    hasThromboembolism : getters.THROMBOEMBOLISM === 'YES',
    hasHistoryTIA : getters.TIA === 'YES',
    hasHistoryStroke : getters.STROKE === 'YES'
  }).then((CHA2DS2VASC) => {
    return dispatch('SET_CHA2DS2_VASC', CHA2DS2VASC.score);
  });
}

export async function getCHA2DS2VASC({ dispatch, commit, rootGetters }, {
  patientId,
  age = null,
  gender = null,
  hasCongestiveHeartFailure = null,
  hasHistoryHypertension = null,
  hasHistoryDiabetes  = null,
  hasThromboembolism = null,
  hasHistoryTIA = null,
  hasHistoryStroke = null,
  hasStenosisInCoronaryArteries = null
}) {
  try {
    const { data, errors } = await app.apolloClient.query({
      query: medHistoryQueries.CHA2DS2VASC,
      variables: {
        patientId : parseInt(patientId),
        age : parseInt(age),
        gender,
        hasCongestiveHeartFailure ,
        hasHistoryHypertension,
        hasHistoryDiabetes,
        hasThromboembolism,
        hasHistoryTIA,
        hasHistoryStroke,
        hasStenosisInCoronaryArteries
      },
    });

    if (data && !errors) {
      return data.CHA2DS2VASC;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getQuestionnaire({ dispatch, commit, getters, rootGetters }, key) {
  const { patientId, version } = getters;

  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.getQuestionnaire,
      variables: { patientId : patientId.toString(), version : version.toString(), key },
    });

    if (data && !errors) {
      const questionnaire = data.myQuestionnaireForms
          .find((item) => item.questionnaire.key === key);

      if (questionnaire && questionnaire.entries) {
        questionnaire.entries.forEach((entry) => {
          if (entry.givenAnswers && entry.givenAnswers.length) {
            if (getClinicalAssessmentQuestion(entry.question.key) || getMedicationDSSQuestion(entry.question.key)){
              commit(`SET_${entry.question.key}`, entry.givenAnswers[0].key ? entry.givenAnswers[0].key : entry.givenAnswers[0].openAnswer);
            }
          }
        });
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getQuestionnaireV1V2({ dispatch, commit, getters, rootGetters }, key) {
  const { patientId } = getters;

  for (let version=1; version<=2; version++) {
    try {
      const { data, errors } = await app.apolloMobileClient.query({
        query: queries.getQuestionnaire,
        variables: { patientId : patientId.toString(), version: version.toString(), key },
      });

      if (data && !errors) {
        const questionnaire = data.myQuestionnaireForms
            .find((item) => item.questionnaire.key === key);

        if (questionnaire && questionnaire.entries) {
          questionnaire.entries.forEach((entry) => {
            if (entry.givenAnswers && entry.givenAnswers.length) {
              if (getClinicalAssessmentQuestion(entry.question.key) || getMedicationDSSQuestion(entry.question.key)){
                commit(`SET_${entry.question.key}_V${version}`, entry.givenAnswers[0].key ? entry.givenAnswers[0].key : entry.givenAnswers[0].openAnswer);
              }
            }
          });
        }
      } else if (errors.length) {
        errors.forEach((error) => {
          dispatch('error/submit', error, { root: true });
        });
      }
    } catch (error) {
      dispatch('error/submit', error, { root: true });
    }

  }
}

export function setCurrentAmountReceivedHypertensionDrugs({ commit }, currentAmountReceivedHypertensionDrugs) {
  commit('currentAmountReceivedHypertensionDrugs', currentAmountReceivedHypertensionDrugs);
}

export function setMeanBloodPressureAboveGoal({ commit }, meanBloodPressureAboveGoal) {
  commit('meanBloodPressureAboveGoal', meanBloodPressureAboveGoal);
}

export function setBodyWeight({ commit }, bodyWeight) {
  commit('setBodyWeight', bodyWeight);
}

export function setBloodPressureSystolic({ commit }, systolic) {
  commit('setBloodPressureSystolic', systolic);
}

export function setBloodPressureDiastolic({ commit }, diastolic) {
  commit('setBloodPressureDiastolic', diastolic);
}

export function setPulseRate({ commit }, pulseRate) {
  commit('setPulseRate', pulseRate);
}

export function setLDL({ commit }, LDL) {
  commit('setLDL', LDL);
}

export function setLDLInPreferredUnit({ commit }, LDLInPreferredUnit) {
  commit('setLDLInPreferredUnit', LDLInPreferredUnit);
}

export function setEGFR({ commit }, eGFR) {
  commit('setEGFR', eGFR);
}

export function setDistanceWalked({ commit }, distanceWalked) {
  commit('setDistanceWalked', distanceWalked);
}

export function setPerceivedExertion({ commit }, perceivedExertion) {
  commit('setPerceivedExertion', perceivedExertion);
}

export function setIsWalkingTestPerformed({ commit }, isWalkingTestPerformed) {
  commit('setIsWalkingTestPerformed', isWalkingTestPerformed);
}

export function setNotPerformedReasonGroup({ commit }, notPerformedReasonGroup) {
  commit('setNotPerformedReasonGroup', notPerformedReasonGroup);
}

export function setNotPerformedReason({ commit }, notPerformedReason) {
  commit('setNotPerformedReason', notPerformedReason);
}

export function switchToValidationMode({ commit }) {
  commit('switchToValidationMode');
}

export function switchToNormalMode({ commit, getters }) {
  if (getters.isValidationMode) {
    commit('switchToNormalMode');
  }
}

export async function submit({ dispatch, getters }) {
  // submit vital signs before rest. Patient weight for vital signs has impact on calculations for walking test,
  // need to make sure backend has access to latest weight of patient
  const resultsVitalSigns = [await dispatch('submitVitalSigns')];

  // submit walking test only in v2, 6, 7 - step is disabled for rest of the visits
  const walkingTestEnabled =
    parseInt(getters.version) === 2 ||
    parseInt(getters.version) === 6 ||
    parseInt(getters.version) === 7;

  const resultsRest = await Promise.all(
    walkingTestEnabled
    ? [
        dispatch('submitWalkingTest'),
        dispatch('submitClinicalAssessment'),
        dispatch('submitMedicationDSS'),
      ]
    : [
        dispatch('submitClinicalAssessment'),
        dispatch('submitMedicationDSS'),
      ]
  );

  const results = resultsVitalSigns.concat(resultsRest);

  return (results.every((item) => item));
}

export async function submitVitalSigns({ dispatch, getters }) {
  try {
    const isVersion6 = parseInt(getters.version) === 6;

    const variables = {
      patientId: parseInt(getters.patientId),
      version: parseInt(getters.version),
      weight: parseFloat(getters.bodyWeight),
      systolic: parseInt(getters.bloodPressureSystolic),
      diastolic: parseInt(getters.bloodPressureDiastolic),
      pulseRate: parseFloat(getters.pulseRate),
    };
    let query = queries.submitVitalSigns;

    if (isVersion6) {
      variables['eGFR'] = parseFloat(getters.eGFR);
      variables['LDL'] = parseFloat(getters.LDLInPreferredUnit);
      variables['LDLUnit'] = app.store.getters['patientSummary/cholesterolLDLPreferredUnit']
      query = queries.submitVitalSignsV6;
    }

    const { data, errors } = await app.apolloMobileClient.query({
      query, variables
    });

    if (data && !errors) {
      return true;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });

      return false;
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export async function submitWalkingTest({ dispatch, getters }) {
  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.submitWalkingTest,
      variables: {
        patientId: parseInt(getters.patientId),
        version: parseInt(getters.version),
        notPerformedReason: getters.notPerformedReason || null,
        ...(getters.distanceWalked && {
          distanceWalked: getters.distanceWalked ? parseInt(getters.distanceWalked) : 0,
        }),
        ...(getters.perceivedExertion && {
          perceivedExertion: getters.perceivedExertion
        }),
      },
    });

    if (data && !errors) {
      return true;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });

      return false;
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export async function submitClinicalAssessment({ dispatch, getters }) {
  // pick up fields that are configured to post
  const payload = getters.getEncounterConfig('clinicalAssessment', 'post').reduce((accumulator, currentItem) => {
    let answer;

    // If field is not displayed set answer to null
    if (getters.getEncounterConfig('clinicalAssessment.' + currentItem.key, 'display'))
      answer = getters[currentItem.key]
    else
      answer = null

    // Include given answer in post
    return { ...accumulator, [currentItem.key]: answer };
  }, {});

  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.submitQestionnaire,
      variables: {
        patientId: parseInt(getters.patientId),
        version: parseInt(getters.version),
        data: JSON.stringify(payload),
        key: 'CLINICAL-ASSESSMENT',
      },
    });

    if (data && !errors) {
      return true;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });

      return false;
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export async function submitMedicationDSS({ dispatch, getters }) {
  // pick up fields that are configured to post
  const payload = getters.getEncounterConfig('medicationDSS', 'post').reduce((accumulator, currentItem) => {
    let answer;

    // If field is not displayed set answer to null
    if (getters.getEncounterConfig('medicationDSS.' + currentItem.key, 'display'))
      answer = getters[currentItem.key]
    else
      answer = null

    // Include given answer in post
    return { ...accumulator, [currentItem.key]: answer };
  }, {});

  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.submitQestionnaire,
      variables: {
        patientId: parseInt(getters.patientId),
        version: parseInt(getters.version),
        data: JSON.stringify(payload),
        key: 'DSS-INFO',
      },
    });

    if (data && !errors) {
      return true;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });

      return false;
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export async function reset({ commit }) {
  commit('reset');
}
